/**
 * SettingsPanel — headless settings sheet.
 *
 * Headless: no `browser.*`, no `chrome.*`, no `theme.ts`, no storage
 * hooks. All data arrives as props; theme + notification toggles +
 * disconnect are wired by the host through callbacks; all i18n is
 * supplied via the `labels` bag. Mirrors the conventions established
 * by `SidePanelNotificationList` / `NotificationListItem`.
 *
 * Regrouped into 3 intent groups plus a quiet device footer
 * (settings-declutter S1-S8):
 *   - General — theme picker + optional language row, always
 *     rendered, plus any `extraSections` anchored with
 *     `placement: "general"`.
 *   - Notifications — rendered when at least one of `osNotifications`,
 *     `callNotifications`, or `dnd` is provided. `dnd` renders its own
 *     row label (`dnd.labels.title`) followed by the headless
 *     `DndEditor`, after the toggles — DND is notification policy, so
 *     it keeps a label of its own rather than an unlabeled button
 *     (settings-declutter §4.3).
 *   - Privacy & security — rendered when `privacy` is provided, or
 *     any `extraSections` are anchored with `placement: "privacy"`.
 *     Also hosts the static encryption row (moved here from the old
 *     About section).
 *   - Standalone `extraSections` (no `placement`) — each still gets
 *     its own section + divider.
 *   - Footer — replaces the old full-section Connection / Send
 *     feedback / About sections with one quiet block: a device
 *     status row + two-step disconnect confirm (when `connection` is
 *     provided), the app version caption, the support-email footer,
 *     and the "Send feedback" link. Renders when any of
 *     `connection` / `version` / `supportEmail` / `onSendFeedback`
 *     is provided.
 *
 * `extraSections` optionally render as a collapsed disclosure
 * (`collapsed: true` + `title`) — content mounts only while expanded;
 * `onToggle` notifies the host of the expand/collapse edge.
 *
 * State that stays inside the component:
 *   - `confirming` / `disconnecting` (two-step disconnect dialog).
 *     The actual disconnect IPC is the host's `onDisconnect()`
 *     returning a Promise. If it throws, the confirm dialog resets
 *     so the user lands back on the primary button rather than a
 *     stuck "loading" Yes button.
 *   - `expandedSections` (which collapsed extra sections are open).
 *
 * The host owns: theme persistence, theme application
 * (document.documentElement attribute), notification toggle
 * persistence, DND snooze persistence, the actual disconnect IPC,
 * and all i18n.
 */

import type { ReflectoStorage } from "@reflecto/client-core";
import { ChevronDown, ChevronLeft, Contrast01, Moon01, Sun } from "@untitledui/icons";
import { type ReactNode, useCallback, useId, useRef, useState } from "react";

/** Clicks on the version caption that complete the hidden internal-tester gesture. */
const SECRET_GESTURE_TAPS = 7;
/** Max gap between those clicks; a longer pause restarts the streak. */
const SECRET_GESTURE_WINDOW_MS = 3_000;

import { splitOnEmailSentinel } from "../../utils/emailSentinel";
import { Button } from "../Button";
import { DndEditor, type DndEditorLabels } from "../DndEditor";
import { IconButton } from "../IconButton";
import { Link } from "../Link";
import { StatusDot } from "../StatusDot";
import { Tab, TabGroup } from "../TabGroup";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import styles from "./SettingsPanel.module.css";

/**
 * Derived from the canonical storage schema in `@reflecto/client-core`
 * so adding a new theme value (e.g. "high-contrast") forces a compile
 * error in every consumer at once — both the extension wrapper and the
 * web wrapper share this type, so an independent local union here
 * would silently drift apart from the schema and from each other.
 *
 * Type-only import — Rollup erases it at build time, so this doesn't
 * pull `@reflecto/client-core`'s runtime into the client-ui bundle
 * (matches the existing pattern for `ConversationThread` /
 * `DecryptedNotification` imports across this package).
 */
export type ThemeValue = ReflectoStorage["theme"];

/**
 * i18n bag for every visible string in the panel.
 *
 * `needHelp` is optional — when present alongside `supportEmail`, the
 * footer renders the copy with the email anchor inlined at the
 * `__EMAIL__` sentinel. When absent (or `supportEmail` is missing),
 * the support line is omitted.
 */
export interface SettingsPanelLabels {
  back: string;
  settings: string;
  general: string;
  theme: string;
  themeAuto: string;
  themeLight: string;
  themeDark: string;
  notificationsSection: string;
  osNotifications: string;
  callNotifications: string;
  statusConnected: string;
  statusReconnecting: string;
  statusDisconnected: string;
  disconnectFromPhone: string;
  disconnectConfirm: string;
  yesDisconnect: string;
  cancel: string;
  version: string;
  encryption: string;
  encryptionValue: string;
  /** Optional. Should contain `__EMAIL__` sentinel — replaced with the supportEmail anchor inline. */
  needHelp?: string;
  /** Optional — label for the "Send feedback" entry. Renders only when `onSendFeedback` is also provided. */
  sendFeedback?: string;
}

/**
 * Toggle config — bundles the controlled value with its onChange.
 * Mirrors the IconButton pattern of grouping related props so the
 * SettingsPanel prop list stays readable as more sections are added.
 */
export interface SettingsPanelToggle {
  value: boolean;
  onChange(next: boolean): void;
}

/**
 * Connection section config. `paired` gates whether the disconnect
 * button renders at all; `connected` drives the status chip
 * (Connected / Reconnecting / Disconnected). `onDisconnect` is async
 * — the headless component manages the disconnecting/confirming
 * state machine and resets on failure (caught Promise rejection).
 */
export interface SettingsPanelConnectionConfig {
  paired: boolean;
  connected: boolean;
  /** Async — the wrapper handles the IPC / service call. Errors caught + reset by the headless component. */
  onDisconnect(): Promise<void>;
}

/**
 * i18n for the Privacy section. Bundled with `SettingsPanelPrivacyConfig`
 * (rather than folded into the shared `SettingsPanelLabels`) so these keys
 * are only ever required when a host actually wires the section — adding
 * them to the always-required `SettingsPanelLabels` would force every
 * existing consumer (both apps' label bags, every test fixture, every
 * story) to grow new required fields just to keep compiling.
 */
export interface SettingsPanelPrivacyLabels {
  /** Section heading, e.g. "Privacy". */
  section: string;
  /** Toggle label, e.g. "Anonymous usage statistics". Checked = participating. */
  analyticsToggle: string;
  /** Caption rendered under the toggle explaining what is/isn't captured. */
  analyticsToggleDescription: string;
}

/**
 * Privacy section config. `analyticsEnabled.value` is the PARTICIPATING
 * state (checked = collecting anonymous analytics), i.e. the inverse of
 * the host's persisted `analyticsOptOut` flag — the headless component
 * never sees the storage key name, only the boolean the switch should
 * show. `labels` is required here (not on the shared bag) — see the
 * type doc above.
 */
export interface SettingsPanelPrivacyConfig {
  analyticsEnabled: SettingsPanelToggle;
  labels: SettingsPanelPrivacyLabels;
}

/**
 * DND (Do Not Disturb) slot config. Rendered inside the Notifications
 * section, after the toggles, when provided — mirrors the shape the
 * headless `DndEditor` expects. `labels` is required here (not folded
 * into the shared `SettingsPanelLabels`) for the same reason as
 * `SettingsPanelPrivacyLabels` above: only hosts that actually wire
 * DND grow new required keys.
 */
export interface SettingsPanelDndConfig {
  /** Epoch ms timestamp; if > now the snooze is active. Forwarded to `DndEditor`. */
  snoozeUntil?: number;
  onSnoozeFor(durationMs: number): void;
  onCancelSnooze(): void;
  labels: DndEditorLabels;
}

/**
 * Slot config for a host-supplied section. The headless SettingsPanel
 * stays decoupled from feature-specific panels (Devices, Hidden Apps,
 * safety numbers, …) by exposing them as `extraSections` — the host
 * renders each section's body via `content` and the panel supplies
 * the matching chrome (SectionHeader + divider, or a collapsed
 * disclosure row) so the new section visually slots into the existing
 * rhythm.
 *
 * By default a section renders standalone, in array order, after the
 * Privacy & security group and before the footer. `placement` anchors
 * it into the General or Privacy & security group instead — anchored
 * sections render inside that group's `<section>`, with no divider of
 * their own. `id` exists purely as a stable React key (and as the
 * disclosure's expanded-state key when `collapsed` is set).
 */
export interface SettingsPanelExtraSection {
  /** Stable React key for the section. */
  id: string;
  /** Localized section heading shown above `content`. Omit to let `content` supply its own heading. */
  title?: string;
  /** Section body — rendered as-is inside the section wrapper. */
  content: ReactNode;
  /** Anchor into a group instead of a standalone section. */
  placement?: "general" | "privacy";
  /** Render as a collapsed disclosure row (title required); content mounts only while expanded. */
  collapsed?: boolean;
  /** Fires on disclosure toggle (only meaningful with `collapsed`). */
  onToggle?(expanded: boolean): void;
}

export interface SettingsPanelLanguageOption {
  /** Catalog registry tag ("de", "pt-BR", …). */
  tag: string;
  /** The language's self-name ("Deutsch", "日本語") — never translated. */
  endonym: string;
}

/**
 * Language-picker config. Optional — hosts that don't wire a language
 * setting keep compiling unchanged (the Privacy-section precedent).
 * Rendered as a row in the Appearance section, below the theme picker.
 */
export interface SettingsPanelLanguageConfig {
  /** Current override tag, or null when following the system language. */
  value: string | null;
  /** Selectable locales, endonym-labeled; host derives from its catalog registry. */
  options: SettingsPanelLanguageOption[];
  /** `null` = the user chose "System default". */
  onChange(tag: string | null): void;
  labels: {
    /** Row label, e.g. "Language". */
    language: string;
    /** First option — follow the system language. */
    systemDefault: string;
  };
}

export interface SettingsPanelProps {
  // ── Theme — always required ──────────────────────────────────────
  theme: ThemeValue;
  onThemeChange(theme: ThemeValue): void;

  // ── Notifications — pass undefined to hide entire section ────────
  osNotifications?: SettingsPanelToggle;
  callNotifications?: SettingsPanelToggle;
  /** DND snooze slot — rendered inside the Notifications section, after the toggles. */
  dnd?: SettingsPanelDndConfig;

  // ── Language — pass undefined to hide the picker row ─────────────
  language?: SettingsPanelLanguageConfig;

  // ── Privacy & security — pass undefined to hide the toggle+description ──
  privacy?: SettingsPanelPrivacyConfig;

  // ── Connection — drives the footer's device status row + disconnect ────
  connection?: SettingsPanelConnectionConfig;

  // ── Extra sections (e.g. Devices, safety numbers) ─────────────────
  /**
   * Host-supplied sections. Each anchors into the General or Privacy
   * & security group via `placement`, or (when omitted) renders
   * standalone after the Privacy & security group and before the
   * footer. The host owns the body content (e.g. headless
   * `DndEditor`, a device list). Pass undefined or [] to render
   * nothing extra.
   */
  extraSections?: SettingsPanelExtraSection[];

  // ── Footer — version caption always shown when provided ──────────
  version?: string;
  /**
   * Fired when the version caption is clicked `SECRET_GESTURE_TAPS` times in quick succession —
   * the Android build-number idiom. Hosts use it to flag the machine as one of their own test
   * devices, so its analytics stop counting towards the product metrics.
   *
   * Omit it and the caption stays completely inert. Deliberately carries no visual affordance,
   * no cursor change and no button role: this is meant to stay invisible to everyone who does
   * not already know it exists.
   */
  onSecretGesture?: () => void;
  /**
   * Email address rendered as a `mailto:` link in the support footer
   * (when `labels.needHelp` is also provided). The host is responsible
   * for passing a SAFE value — no `javascript:`, `data:`, or
   * untrusted-user-input URLs. React 16.9+ strips `javascript:` hrefs
   * defensively, but the contract here is that consumers hardcode (or
   * server-validate) the address; this component performs no
   * additional sanitisation.
   */
  supportEmail?: string;

  /** Optional — opens the host's feedback dialog. Renders a "Send feedback" entry when `labels.sendFeedback` is also set. */
  onSendFeedback?: () => void;

  onBack(): void;
  labels: SettingsPanelLabels;
}

export function SettingsPanel({
  theme,
  language,
  onThemeChange,
  osNotifications,
  callNotifications,
  dnd,
  privacy,
  connection,
  extraSections,
  version,
  onSecretGesture,
  supportEmail,
  onSendFeedback,
  onBack,
  labels,
}: SettingsPanelProps) {
  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  // Refs, not state: the streak must never trigger a re-render, and it is read and written
  // synchronously within a single click handler.
  const secretStreak = useRef(0);
  const secretLastClickAt = useRef(0);
  const handleVersionClick = useCallback(() => {
    const now = Date.now();
    const continues =
      secretStreak.current > 0 && now - secretLastClickAt.current <= SECRET_GESTURE_WINDOW_MS;
    secretStreak.current = continues ? secretStreak.current + 1 : 1;
    secretLastClickAt.current = now;
    if (secretStreak.current < SECRET_GESTURE_TAPS) return;
    secretStreak.current = 0;
    onSecretGesture?.();
  }, [onSecretGesture]);
  // One expanded-id set for collapsed extra sections; reset is not needed —
  // the panel unmounts when the host navigates away.
  const [expandedSections, setExpandedSections] = useState<ReadonlySet<string>>(new Set());
  const toggleSection = useCallback(
    (section: SettingsPanelExtraSection) => {
      // `expanded` is derived from the current render's `expandedSections`
      // BEFORE the state update, and `onToggle` fires OUTSIDE the
      // `setExpandedSections` updater. React updaters must be pure —
      // both hosts render this component under `<StrictMode>`, which
      // double-invokes updaters in dev, so a side effect inside the
      // updater (e.g. a host's analytics capture in `onToggle`) would
      // fire twice per click. The updater below only derives the next
      // Set from `prev`.
      const expanded = !expandedSections.has(section.id);
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (expanded) next.add(section.id);
        else next.delete(section.id);
        return next;
      });
      section.onToggle?.(expanded);
    },
    [expandedSections]
  );
  // Stable ids so the visible Text label and the Toggle's internal
  // <input> share an `htmlFor`/`id` pair — clicking the label text
  // activates the switch for sighted users.
  const osToggleId = useId();
  const callToggleId = useId();
  const analyticsToggleId = useId();
  const themeLabelId = useId();
  const languageSelectId = useId();

  const handleDisconnect = useCallback(async () => {
    if (!connection) return;
    setDisconnecting(true);
    try {
      await connection.onDisconnect();
    } catch {
      // Reset the confirm dialog on error so the user lands back on
      // the primary disconnect button instead of a stale confirm
      // row with re-enabled Yes/Cancel. Errors themselves are the
      // host's responsibility to log/surface (this package stays
      // logging-free per CLAUDE.md).
      setDisconnecting(false);
      setConfirming(false);
      return;
    }
    setDisconnecting(false);
    setConfirming(false);
    // Narrow the dep to the function ref rather than the whole
    // `connection` prop. Both host wrappers pass `connection` as an
    // inline object literal that gets a fresh reference on every
    // render (and `paired` / `connected` re-render on every SSE
    // status toggle via `useStorage`). Depending on the whole
    // object would defeat the memoization on the SSE-flap hot path;
    // depending on the function ref keeps it stable across status
    // changes — host wrappers should still keep `onDisconnect` itself
    // stable via `useCallback`.
  }, [connection?.onDisconnect, connection]);

  const showNotificationsSection =
    osNotifications !== undefined || callNotifications !== undefined || dnd !== undefined;

  // Anchor extras into their group; sections with no `placement` stay
  // standalone (rendered after Privacy & security, before the footer).
  const generalExtras = extraSections?.filter((section) => section.placement === "general") ?? [];
  const privacyExtras = extraSections?.filter((section) => section.placement === "privacy") ?? [];
  const standaloneExtras = extraSections?.filter((section) => !section.placement) ?? [];
  const showPrivacySection = privacy !== undefined || privacyExtras.length > 0;

  // The footer replaces the old Connection / Send feedback / About
  // sections with one quiet block — render it whenever any of its
  // pieces has something to show. Mirrors the render guards each
  // child actually uses below (not just prop presence): the support
  // row needs BOTH `supportEmail` and `labels.needHelp` (see
  // `supportFooter` below), and the feedback link needs BOTH
  // `onSendFeedback` and `labels.sendFeedback` — a host that sets one
  // without the other must not get a bare divider over empty padding.
  const showFooter = Boolean(
    connection !== undefined ||
      version !== undefined ||
      (supportEmail && labels.needHelp) ||
      (onSendFeedback && labels.sendFeedback)
  );

  // Message carries the shared __EMAIL__ sentinel (utils/emailSentinel.ts);
  // split to inject the <Link> at the placeholder position.
  let supportFooter: ReactNode = null;
  if (supportEmail && labels.needHelp) {
    const { before: needHelpBefore, after: needHelpAfter } = splitOnEmailSentinel(labels.needHelp);
    supportFooter = (
      <Text as="div" size="s" tone="tertiary" className={styles.supportRow}>
        {needHelpBefore}
        <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>
        {needHelpAfter}
      </Text>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <IconButton
          icon={ChevronLeft}
          label={labels.back}
          variant="default"
          size="m"
          onClick={onBack}
        />
        <Text as="h2" size="m" weight="semibold" className={styles.title}>
          {labels.settings}
        </Text>
      </header>

      <div className={styles.content}>
        {/* ─── General ────────────────────────────────────────── */}
        <section className={styles.section}>
          <SectionHeader>{labels.general}</SectionHeader>
          <div className={styles.row}>
            <Text id={themeLabelId} size="s" weight="medium">
              {labels.theme}
            </Text>
            {/* Icon-only TabGroup per Figma spec — contrast / sun / moon
             *  map to Auto / Light / Dark. aria-labelledby points at the
             *  visible "Theme" label so screen readers read the same
             *  string; per-tab aria-labels announce the theme names. */}
            <TabGroup
              value={theme}
              // TabGroup's `onChange` returns the raw `value` string —
              // narrow back to the `ThemeValue` union since we control
              // the Tab values just below (auto / light / dark).
              onChange={(v) => onThemeChange(v as ThemeValue)}
              aria-labelledby={themeLabelId}
            >
              <Tab value="auto" icon={Contrast01} aria-label={labels.themeAuto} />
              <Tab value="light" icon={Sun} aria-label={labels.themeLight} />
              <Tab value="dark" icon={Moon01} aria-label={labels.themeDark} />
            </TabGroup>
          </div>
          {language && (
            <div className={styles.row}>
              <label className={styles.rowLabel} htmlFor={languageSelectId}>
                <Text size="s" weight="medium">
                  {language.labels.language}
                </Text>
              </label>
              {/* Native <select>: 20+ options rule out TabGroup and
               *  client-ui ships no Select primitive. The empty-string
               *  value encodes "System default" (null override) since
               *  <option> values must be strings. */}
              <select
                id={languageSelectId}
                className={styles.languageSelect}
                value={language.value ?? ""}
                onChange={(e) => language.onChange(e.target.value === "" ? null : e.target.value)}
              >
                <option value="">{language.labels.systemDefault}</option>
                {language.options.map((o) => (
                  <option key={o.tag} value={o.tag}>
                    {o.endonym}
                  </option>
                ))}
              </select>
            </div>
          )}
          {generalExtras.map((section) => (
            <ExtraSectionBody
              key={section.id}
              section={section}
              expanded={expandedSections.has(section.id)}
              onToggle={toggleSection}
            />
          ))}
        </section>

        {showNotificationsSection && (
          <>
            <SectionDivider />

            {/* ─── Notifications ──────────────────────────────────── */}
            <section className={styles.section}>
              <SectionHeader>{labels.notificationsSection}</SectionHeader>
              {osNotifications && (
                <div className={styles.row}>
                  {/* Sibling <label htmlFor> points at the same input id
                   *  the Toggle uses internally — clicking the visible
                   *  text activates the switch. (Outer label wrapping the
                   *  row would nest inside Toggle's own <label>; using
                   *  htmlFor sidesteps the invalid-HTML problem.)
                   *
                   *  Two `<label>` elements end up associated with the
                   *  same input id: this sibling one (visible text) +
                   *  the Toggle's internal `<label>` wrapper (track +
                   *  thumb). Both targets activate the input via native
                   *  click-to-toggle, but the input's accessible NAME
                   *  comes from `aria-label` alone (per ARIA precedence:
                   *  aria-labelledby > aria-label > <label> association),
                   *  so AT announces the toggle once — not twice. */}
                  <label className={styles.rowLabel} htmlFor={osToggleId}>
                    <Text size="s" weight="medium">
                      {labels.osNotifications}
                    </Text>
                  </label>
                  <Toggle
                    id={osToggleId}
                    size="lg"
                    checked={osNotifications.value}
                    onChange={osNotifications.onChange}
                    aria-label={labels.osNotifications}
                  />
                </div>
              )}
              {callNotifications && (
                <div className={styles.row}>
                  <label className={styles.rowLabel} htmlFor={callToggleId}>
                    <Text size="s" weight="medium">
                      {labels.callNotifications}
                    </Text>
                  </label>
                  <Toggle
                    id={callToggleId}
                    size="lg"
                    checked={callNotifications.value}
                    onChange={callNotifications.onChange}
                    aria-label={labels.callNotifications}
                  />
                </div>
              )}
              {dnd && (
                <>
                  {/* Row label above the DndEditor — not a SectionHeader
                   *  (DND stays inside the Notifications group, it isn't
                   *  its own group), same weight as the toggle row labels
                   *  above it so "Snooze 1 hour" isn't the loudest
                   *  unlabeled element in the section (spec §4.3). */}
                  <div className={styles.dndTitleRow}>
                    <Text size="s" weight="medium">
                      {dnd.labels.title}
                    </Text>
                  </div>
                  <DndEditor
                    snoozeUntil={dnd.snoozeUntil}
                    onSnoozeFor={dnd.onSnoozeFor}
                    onCancelSnooze={dnd.onCancelSnooze}
                    labels={dnd.labels}
                  />
                </>
              )}
            </section>
          </>
        )}

        {showPrivacySection && (
          <>
            <SectionDivider />

            {/* ─── Privacy & security ─────────────────────────────── */}
            <section className={styles.section}>
              {/* Hoisted out of the toggle body below: the group's
               *  `<section>` also renders for a `placement:"privacy"`
               *  extra anchored with no `privacy` config at all (see
               *  `showPrivacySection`), and the header must show
               *  whenever `privacy` supplies one — independent of
               *  whether the toggle body also renders. The section
               *  title only ever exists on `privacy.labels.section`
               *  (by design — see `SettingsPanelPrivacyLabels`'s own
               *  doc, kept off the always-required shared `labels` bag),
               *  so a group with ONLY extras and no `privacy` falls back
               *  gracefully to no header rather than inventing a new
               *  required prop just for that title string. */}
              {privacy && <SectionHeader>{privacy.labels.section}</SectionHeader>}
              {privacy && (
                <>
                  <div className={styles.row}>
                    <label className={styles.rowLabel} htmlFor={analyticsToggleId}>
                      <Text size="s" weight="medium">
                        {privacy.labels.analyticsToggle}
                      </Text>
                    </label>
                    <Toggle
                      id={analyticsToggleId}
                      size="lg"
                      checked={privacy.analyticsEnabled.value}
                      onChange={privacy.analyticsEnabled.onChange}
                      aria-label={privacy.labels.analyticsToggle}
                    />
                  </div>
                  <Text as="p" size="xs" tone="tertiary" className={styles.rowDescription}>
                    {privacy.labels.analyticsToggleDescription}
                  </Text>
                </>
              )}
              {/* Static encryption row — moved here from the old About
               *  section; it's a security fact, not app trivia. */}
              <div className={styles.row}>
                <Text size="s" weight="medium">
                  {labels.encryption}
                </Text>
                <Text size="s" tone="tertiary">
                  {labels.encryptionValue}
                </Text>
              </div>
              {privacyExtras.map((section) => (
                <ExtraSectionBody
                  key={section.id}
                  section={section}
                  expanded={expandedSections.has(section.id)}
                  onToggle={toggleSection}
                />
              ))}
            </section>
          </>
        )}

        {standaloneExtras.map((section) => (
          <div key={section.id}>
            <SectionDivider />
            <section className={styles.section}>
              <ExtraSectionBody
                section={section}
                expanded={expandedSections.has(section.id)}
                onToggle={toggleSection}
              />
            </section>
          </div>
        ))}

        {showFooter && (
          <>
            <SectionDivider />
            <div className={styles.footer}>
              {connection && (
                <div className={styles.deviceRow}>
                  <span className={styles.deviceStatus}>
                    {connection.connected && <StatusDot />}
                    <Text size="s" tone={connection.connected ? "secondary" : "tertiary"}>
                      {connection.connected
                        ? labels.statusConnected
                        : connection.paired
                          ? labels.statusReconnecting
                          : labels.statusDisconnected}
                    </Text>
                  </span>
                  {connection.paired && !confirming && (
                    // Demoted destructive (spec S4): this is a once-ever verb
                    // sitting in a footer, so it must be reachable without
                    // dominating the page. The confirm step below keeps the
                    // loud filled `negative` — that IS the commit.
                    <Button
                      variant="negative-ghost"
                      size="s"
                      onClick={() => setConfirming(true)}
                      disabled={disconnecting}
                    >
                      {labels.disconnectFromPhone}
                    </Button>
                  )}
                </div>
              )}
              {connection?.paired && confirming && (
                // Two-step confirm flow retained per product requirement
                // (safety net against accidental taps) even though the
                // Figma spec only shows the bare button.
                <div className={styles.confirmRow}>
                  <Text size="s" tone="secondary">
                    {labels.disconnectConfirm}
                  </Text>
                  <div className={styles.confirmActions}>
                    <Button
                      variant="negative"
                      size="s"
                      className={styles.confirmAction}
                      loading={disconnecting}
                      onClick={handleDisconnect}
                    >
                      {labels.yesDisconnect}
                    </Button>
                    <Button
                      variant="secondary"
                      size="s"
                      className={styles.confirmAction}
                      disabled={disconnecting}
                      onClick={() => setConfirming(false)}
                    >
                      {labels.cancel}
                    </Button>
                  </div>
                </div>
              )}
              {version !== undefined && (
                <Text
                  as="p"
                  size="xs"
                  tone="tertiary"
                  className={styles.footerCaption}
                  onClick={onSecretGesture ? handleVersionClick : undefined}
                >
                  {labels.version} {version}
                </Text>
              )}
              {supportFooter}
              {onSendFeedback && labels.sendFeedback && (
                <button type="button" className={styles.footerLink} onClick={onSendFeedback}>
                  <Text size="xs" tone="link">
                    {labels.sendFeedback}
                  </Text>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Text as="h3" size="xs" weight="semibold" tone="tertiary" className={styles.sectionHeader}>
      {children}
    </Text>
  );
}

function SectionDivider() {
  return <div className={styles.sectionDivider} aria-hidden="true" />;
}

/**
 * Renders one `SettingsPanelExtraSection`'s body — either the plain
 * heading+content shape (default) or a collapsed disclosure row whose
 * `content` mounts only while `expanded`. Used for every extra
 * section regardless of where it lands (General, Privacy & security,
 * or standalone) so the collapsed behavior is identical everywhere.
 */
function ExtraSectionBody({
  section,
  expanded,
  onToggle,
}: {
  section: SettingsPanelExtraSection;
  expanded: boolean;
  onToggle: (section: SettingsPanelExtraSection) => void;
}) {
  // Called unconditionally (rules of hooks — this must run before the
  // early return below, even though its value is only used in the
  // collapsed branch). `useId()` rather than a hand-rolled
  // `settings-disclosure-${section.id}` global DOM id: two mounted
  // panels (or a host that reuses a section id) would otherwise
  // collide on the same id — `useId()` is unique per component
  // instance. `section.id` is still folded in for debuggability.
  const reactId = useId();
  if (!section.collapsed) {
    return (
      <>
        {section.title && <SectionHeader>{section.title}</SectionHeader>}
        <div className={styles.extraContent}>{section.content}</div>
      </>
    );
  }
  const contentId = `${reactId}-${section.id}`;
  return (
    <>
      <button
        type="button"
        className={styles.disclosureRow}
        aria-expanded={expanded}
        // Omitted entirely while collapsed rather than pointing at a
        // not-yet-mounted id — content mounts only on expand (see
        // below), and a dangling `aria-controls` reference fails axe's
        // aria-valid-attr-value rule. Don't "fix" this by pre-rendering
        // a hidden element instead; that would defeat the
        // mount-on-expand requirement this section exists for.
        {...(expanded ? { "aria-controls": contentId } : {})}
        onClick={() => onToggle(section)}
      >
        <Text size="s" weight="medium">
          {section.title}
        </Text>
        <ChevronDown
          className={expanded ? styles.chevronOpen : styles.chevron}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div id={contentId} className={styles.extraContent}>
          {section.content}
        </div>
      )}
    </>
  );
}
